import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { usePostHog, useFeatureFlagVariantKey } from 'posthog-js/react';

import { getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { REDIRECT_TO_SESSION_KEY, CANOPY_SIGN_UP_CALLBACK, CANOPY_LOGIN_CALLBACK } from '@/utils/constants';
import { buildRedirectParam, isSafeRedirectUri } from '@/utils/urlHelpers';
import { logError } from '@/utils/logError';
import { ActiveFeatureFlag, isBooleanFeatureFlagEnabled } from '@/hooks/usePostHogFeatureFlags';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity } from '@/utils/posthog';

const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const SleepioEmailCallback: React.FC = () => {
  const history = useHistory();
  const { lob } = useParams<{ lob: string }>();
  const { search } = useLocation();
  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const supabaseAuthEnabled = isBooleanFeatureFlagEnabled(
    useFeatureFlagVariantKey(ActiveFeatureFlag.CAP_USE_SUPABASE_AUTH),
  );

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const redirectToParam = new URLSearchParams(search).get('redirectTo');
        if (redirectToParam && isSafeRedirectUri(redirectToParam)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
        }

        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
          return;
        }

        if (supabaseAuthEnabled === null) { return; }

        if (!supabaseAuthEnabled) {
          history.replace('/activate/sleepio/employers');
          return;
        }

        const entitlementStatus = getEntitlementStatus(session.access_token);
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;
        const isReturningUser = !accessCode && entitlementStatus === 'active';

        const orgSlug = new URLSearchParams(search).get('org_slug') ?? '';
        const intent = isReturningUser ? 'login' : 'sign-up';
        const storedRedirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
        const fallback = isReturningUser ? CANOPY_LOGIN_CALLBACK : CANOPY_SIGN_UP_CALLBACK;
        const effectiveRedirectTo = (storedRedirectTo && isSafeRedirectUri(storedRedirectTo))
          ? storedRedirectTo
          : fallback;
        const redirectParam = buildRedirectParam(effectiveRedirectTo);

        const email = session.user?.email;
        if (email) {
          // Fire-and-forget: React Router navigation keeps the Apollo client alive,
          // so the in-flight query completes after the component unmounts.
          linkPostHogIdentity(getDistinctId, posthog, email, '[SleepioEmailCallback]');
        }

        history.replace(`/activate/sleepio-canopy/${lob}/${orgSlug}/${intent}/email/callback${redirectParam}`);
      } catch (err) {
        logError('[SleepioEmailCallback]', 'Unexpected error', err);
        history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
      }
    };

    handleCallback();
  }, [history, lob, search, supabaseAuthEnabled]);

  return (
    <ThemeProvider theme={getTheme('sleepio')}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default SleepioEmailCallback;
